import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import CustomModal from "../../Common/CustomModal";
import { Check, TaskAltOutlined } from "@mui/icons-material";
import { Checkbox, FormControlLabel } from "@mui/material";
import WheelOfLifeChart from "../../Content/WheelOfLifeChart";
import axiosInstance from "../../../utils/config/config";
import {
  getUserById,
  getWheelOfLife,
} from "../../../redux/actions/user.dispatcher";
import {
  wheelOfLifeCategories,
  wheelOfLifeCategoryColors,
} from "../../../assets/data/data";
import {
  MODAL_TITLE,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  MODAL_WIDTH,
  TOGGLE_LABEL,
  ERROR_MESSAGES,
  ALL_VALUES_ZERO_COUNT,
} from "./WheelOfLifeModal.constants";
import { buildChartData } from "./WheelOfLifeModal.utils";
import {
  ContentBox,
  ToggleLabel,
  checkboxSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
} from "./WheelOfLifeModal.styles";

const WheelOfLifeModal = ({
  open,
  handleClose,
  setloading,
  setsuccess,
  seterrorState,
  setWOLDate,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);

  const [showWheelOfLife, setShowWheelOfLife] = useState(
    userData?.showWheelOfLife || false
  );
  const [chartData, setChartData] = useState(
    buildChartData(wheelOfLifeCategories, wheelOfLifeCategoryColors)
  );
  const [date, setDate] = useState(null);

  const updateUser = async () => {
    try {
      const response = await axiosInstance.put("user/update", {
        showWheelOfLife,
      });
      if (response.status === 200) {
        dispatch(getUserById());
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    updateUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWheelOfLife]);

  const handleSave = async () => {
    seterrorState({ error: false, message: "" });

    if (!date) {
      seterrorState({ error: true, message: ERROR_MESSAGES.SELECT_DATE });
      return;
    }

    try {
      setloading(true);
      const response = await axiosInstance.post("user/wheelOfLife/upsert", {
        date: dayjs(date).format("YYYY-MM-DD"),
        data: chartData,
      });

      if (response.status === 200) {
        setsuccess(true);
        dispatch(getUserById());
        handleClose();
        dispatch(getWheelOfLife());
        setWOLDate(dayjs(date));
      }
    } catch (error) {
      console.error(error);
      seterrorState({ error: true, message: ERROR_MESSAGES.SAVE_FAILED });
    } finally {
      setloading(false);
    }
  };

  const fetchWheelOfLifeData = async (selectedDate) => {
    try {
      const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
      const response = await axiosInstance.get("user/wheelOfLife", {
        params: { date: formattedDate },
      });
      setChartData(
        buildChartData(
          wheelOfLifeCategories,
          wheelOfLifeCategoryColors,
          response.data?.wheelOfLife
        )
      );
    } catch (error) {
      console.error(error);
      setChartData(
        buildChartData(wheelOfLifeCategories, wheelOfLifeCategoryColors)
      );
    }
  };

  useEffect(() => {
    if (date) {
      fetchWheelOfLifeData(date);
    } else {
      setDate(dayjs());
    }
  }, [date]);

  const handleCancel = () => {
    handleClose();
    setChartData(
      buildChartData(wheelOfLifeCategories, wheelOfLifeCategoryColors)
    );
  };

  const isSaveDisabled =
    chartData?.filter((i) => i.value === 0)?.length === ALL_VALUES_ZERO_COUNT;

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      title={MODAL_TITLE}
      saveBtnText={SAVE_BTN_TEXT}
      cancelBtnText={CANCEL_BTN_TEXT}
      width={MODAL_WIDTH}
      saveIcon={Check}
      onSave={handleSave}
      disableBtn={isSaveDisabled}
      onCancel={handleCancel}
    >
      <ContentBox>
        <FormControlLabel
          control={
            <Checkbox
              checked={showWheelOfLife}
              onChange={() => setShowWheelOfLife((prev) => !prev)}
              sx={checkboxSx}
              icon={<TaskAltOutlined sx={checkboxIconSx} />}
              checkedIcon={<TaskAltOutlined sx={checkboxCheckedIconSx} />}
            />
          }
          label={<ToggleLabel>{TOGGLE_LABEL}</ToggleLabel>}
        />

        <WheelOfLifeChart
          chartData={chartData}
          setChartData={setChartData}
          categories={wheelOfLifeCategories}
          categoryColors={wheelOfLifeCategoryColors}
          date={date}
          setDate={setDate}
        />
      </ContentBox>
    </CustomModal>
  );
};

export default WheelOfLifeModal;
